import { useState } from 'react'
import { api } from '../api/client'
import type { ProjectImage } from '../api/types'
import { fileRefusal, imageMeta, sizeLabel, sourceLabel } from '../lib/projectChat'
import { formatDateTime } from '../lib/time'

const message = (failed: unknown) => (failed instanceof Error ? failed.message : 'Something went wrong.')

interface Upload { name: string; progress: number; state: 'sending' | 'done' | 'refused'; note?: string }

/** A project's images (the Projects board, Images tab): uploads, images
 *  attached in Chat and its stations' camera photos, newest first, with a
 *  viewer. Any member uploads, from a phone to a drone or a professional
 *  camera; the original is kept as it was and can be downloaded. */
export function ProjectImages({ projectId, stations, images, selected, onSelect, onChanged }: {
  projectId: number; stations: { code: string; name: string }[]
  images: ProjectImage[] | undefined; selected: string | null
  onSelect: (id: string | null) => void; onChanged: () => void
}) {
  const demo = import.meta.env.VITE_DEMO_MODE === 'true'
  const [station, setStation] = useState('')
  const [uploads, setUploads] = useState<Upload[]>([])
  const [error, setError] = useState<string>()
  const shown = (images ?? []).filter((i) => !station || i.station?.code === station)
  const current = shown.find((i) => i.id === selected) ?? shown[0] ?? null
  const at = current ? shown.indexOf(current) : -1

  const upload = async (files: FileList | null) => {
    if (!files?.length) return
    const list = Array.from(files)
    const rows: Upload[] = list.map((f) => {
      const refusal = fileRefusal(f)
      return { name: f.name, progress: 0, state: refusal ? 'refused' : 'sending', note: refusal ?? undefined }
    })
    setUploads(rows)
    const set = (i: number, patch: Partial<Upload>) =>
      setUploads((now) => now.map((u, k) => (k === i ? { ...u, ...patch } : u)))
    for (const [i, file] of list.entries()) {
      if (rows[i]!.state === 'refused') continue
      try {
        const result = await api.projects.uploadImage(projectId, file, station || undefined,
          (fraction) => set(i, { progress: fraction }))
        const refused = result.refused?.[0]
        set(i, refused ? { state: 'refused', note: refused.reason } : { state: 'done', progress: 1 })
      } catch (failed) {
        set(i, { state: 'refused', note: message(failed) })
      }
    }
    onChanged()
  }

  const remove = async (image: ProjectImage) => {
    try {
      setError(undefined)
      await api.projects.removeImage(projectId, Number(image.id.slice(1)))
      onSelect(null)
      onChanged()
    } catch (failed) {
      setError(message(failed))
    }
  }

  return (
    <div role="tabpanel" aria-label="Images" className="pi-images">
      <div className="pi-tools">
        <label className="field inline">
          <span>Station</span>
          <select value={station} onChange={(e) => { setStation(e.target.value); onSelect(null) }}>
            <option value="">All stations</option>
            {stations.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
          </select>
        </label>
        {!demo && <label className="button pc-up">
          Upload images
          <input type="file" accept="image/*,.heic,.heif" multiple className="visually-hidden"
                 onChange={(e) => { void upload(e.target.files); e.target.value = '' }} />
        </label>}
        <span className="muted pi-hint">
          {demo ? 'Image uploads are available when connected to the file service.' : <>
            {station ? 'Uploads are filed under this station.' : 'Pick a station to file uploads under it.'}
            {' '}JPEG, PNG, WebP, TIFF or HEIC, up to 200 MB each.</>}
        </span>
      </div>

      {uploads.length > 0 && (
        <ul className="pi-uploads" aria-label="Uploads">
          {uploads.map((u, i) => (
            <li key={`${u.name}-${i}`} className={`pi-upload is-${u.state}`}>
              <span className="pi-upload-name">{u.name}</span>
              {u.state === 'sending' && <progress max={1} value={u.progress} aria-label={`Uploading ${u.name}`} />}
              <span className="pi-upload-note">
                {u.state === 'sending' ? `${Math.round(u.progress * 100)}%`
                  : u.state === 'done' ? 'Added' : u.note}
              </span>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="error" role="alert">{error}</p>}

      {images === undefined && <p className="muted">Loading…</p>}
      {current && (
        <figure className="pi-viewer">
          <a href={current.urls.preview} target="_blank" rel="noreferrer" className="pi-view">
            <img src={current.urls.preview} alt={current.caption} />
          </a>
          <figcaption>
            <span className="pi-caption">{current.caption}</span>
            {current.station && <span>{current.station.name}</span>}
            <span>Added {formatDateTime(current.created_at)}{current.by ? ` by ${current.by}` : ''}</span>
            <span className="muted">
              {sourceLabel(current.via)}
              {current.width ? ` · ${current.width} × ${current.height}` : ''}
              {current.bytes ? ` · ${sizeLabel(current.bytes)}` : ''}
            </span>
            <div className="pi-nav">
              <button type="button" className="button" disabled={at <= 0}
                      onClick={() => onSelect(shown[at - 1]!.id)}>Newer</button>
              <button type="button" className="button" disabled={at >= shown.length - 1}
                      onClick={() => onSelect(shown[at + 1]!.id)}>Older</button>
              <span className="muted">{at + 1} of {shown.length}</span>
            </div>
            <div className="pi-links">
              <a href={current.urls.original} download>
                {current.via === 'camera' ? 'Download' : 'Download original'}
              </a>
              {current.can_remove && (
                <button type="button" className="link danger" onClick={() => void remove(current)}>
                  Remove image
                </button>
              )}
            </div>
          </figcaption>
        </figure>
      )}

      {shown.length > 0 && (
        <div className="pi-grid">
          {shown.map((image) => (
            <button key={image.id} type="button" className="pi-thumb"
                    aria-pressed={image.id === current?.id} onClick={() => onSelect(image.id)}>
              <img src={image.urls.thumb} alt="" loading="lazy" />
              <span className="pi-thumb-caption">{image.caption}</span>
              <span className="pi-thumb-meta">{imageMeta(image)}</span>
            </button>
          ))}
        </div>
      )}
      {images !== undefined && shown.length === 0 && (
        <p className="pj-empty">
          No images yet. Upload site photos here or attach them in Chat. A station with a camera
          adds its photos here as they arrive.
        </p>
      )}
    </div>
  )
}
